/**
 * needsYou — the ONE derivation behind the v2 console's Needs-you number
 * (CRM UI streamline D-7).
 *
 * v2 merges four classic triage surfaces (the review queue, the drafts queue,
 * the escalations tab, and the operational flags that were mixed in with them)
 * into a single list. The number on the tab, the number on the KPI tile, and the
 * rows the list actually renders therefore have to come from here and nowhere
 * else — the classic console already learned this the hard way twice: a raw
 * escalation-row count disagreed with the visible queue in BOTH directions
 * (no dedup, and blind to the awaiting-review conversations the queue lists),
 * and a count derived straight from the escalations state dropped to zero the
 * moment somebody browsed Resolved history.
 *
 *   needsYouCount = buildQueueItems(queueEscalations, queueReviews).length
 *                 + drafts.length + suppressed.length
 *                 + activeEsc.opsCount
 *
 * `buildQueueItems` is imported, never re-implemented: it owns the dedup rules
 * (an eligibility escalation folds into its conversation's review row; several
 * screening flags on one conversation roll up to one decision).
 *
 * THE OPS COUNT ARRIVES LATCHED. `opsCount` is computed by the page from its
 * last `status=active` escalations fetch — using `isQueueKind` below — and is
 * NOT re-derived from whatever rows the Needs action / Resolved chips currently
 * hold. That latch is what lets a recruiter read Resolved history without the
 * badge and the tile changing under them.
 */
import {
  buildQueueItems,
  ELIG_KINDS,
  QUESTION_KINDS,
  SCREEN_KINDS,
  type QueueEscalation,
  type QueueItem,
  type QueueReview,
} from '../AgentReviewQueue';
import type { EscalationRow } from '../AgentConsole';
import type { DraftRow } from '../../../components/agent/shared';

/** What one `/agent/drafts` page returns at most — a full page means "and more". */
export const DRAFT_PAGE_CAP = 50;

/** The kinds the review queue itself lists (questions + eligibility + screening). */
const QUEUE_KINDS = new Set<string>([...QUESTION_KINDS, ...ELIG_KINDS, ...SCREEN_KINDS]);

/**
 * Is this escalation kind one the review queue already lists? Everything else
 * is an OPS flag — today `guardrail_block`, `draft_wait`, `error`, `budget` —
 * which nobody makes a determination about but somebody still has to look at.
 *
 * Exported because the page computes the latched `opsCount` with it: one set
 * membership rule, so the count and the "Agent issues" group can never drift.
 */
export function isQueueKind(kind: string): boolean {
  return QUEUE_KINDS.has(kind);
}

/** The ops-kind subset of an escalation list (the "Agent issues" group). */
export function opsEscalations<T extends { kind: string }>(rows: T[]): T[] {
  return rows.filter((row) => !isQueueKind(row.kind));
}

/**
 * The page's latched active-escalation summary. `count` is every active
 * escalation (queue kinds included — it is the classic KPI's number);
 * `opsCount` is the non-queue subset, which is the part Needs-you adds.
 */
export interface ActiveEscSummary {
  count: number;
  capped: boolean;
  opsCount: number;
}

export interface NeedsYou {
  /** Every deduped review-queue work item, oldest first. */
  queueItems: QueueItem[];
  /** `queueItems` partitioned into the list's first three groups. */
  eligibility: QueueItem[];
  screening: QueueItem[];
  questions: QueueItem[];
  /** Drafts waiting: normal copilot drafts first, then guardrail-suppressed. */
  draftItems: DraftRow[];
  /** Either draft page came back full — the total is a floor, render "N+". */
  draftsCapped: boolean;
}

/** One selectable row of the merged list, in the order the list renders them. */
export type NeedsYouRow =
  | { key: string; kind: 'queue'; conversationId: string; item: QueueItem }
  | { key: string; kind: 'draft'; conversationId: string; draft: DraftRow }
  | { key: string; kind: 'ops'; conversationId: string; escalation: EscalationRow };

/** The merged triage list's contents. Pure. */
export function buildNeedsYou(
  queueEscalations: QueueEscalation[],
  queueReviews: QueueReview[],
  drafts: DraftRow[],
  suppressed: DraftRow[],
): NeedsYou {
  const queueItems = buildQueueItems(queueEscalations, queueReviews);
  return {
    queueItems,
    eligibility: queueItems.filter((i) => i.group === 'eligibility'),
    screening: queueItems.filter((i) => i.group === 'screening'),
    questions: queueItems.filter((i) => i.group === 'question'),
    draftItems: [...drafts, ...suppressed],
    draftsCapped: drafts.length >= DRAFT_PAGE_CAP || suppressed.length >= DRAFT_PAGE_CAP,
  };
}

/**
 * The selectable rows, in render order: eligibility · screening · questions ·
 * drafts · agent issues. The list renders exactly these, which is what makes
 * `needsYouCount` below a promise rather than a coincidence.
 *
 * `escalations` is the CURRENT escalations view, raw — the ops filter is applied
 * here rather than trusted to the caller, so a queue-kind row that also appears
 * in that list (a `handoff_review` is both) can never be counted a second time.
 */
export function needsYouRows(needs: NeedsYou, escalations: EscalationRow[]): NeedsYouRow[] {
  return [
    ...needs.eligibility.map((item) => queueRow(item)),
    ...needs.screening.map((item) => queueRow(item)),
    ...needs.questions.map((item) => queueRow(item)),
    ...needs.draftItems.map((draft): NeedsYouRow => ({
      key: `draft-${draft.id}`,
      kind: 'draft',
      conversationId: draft.conversation_id,
      draft,
    })),
    ...opsEscalations(escalations).map((escalation): NeedsYouRow => ({
      key: `ops-${escalation.id}`,
      kind: 'ops',
      conversationId: escalation.conversation_id,
      escalation,
    })),
  ];
}

const queueRow = (item: QueueItem): NeedsYouRow => ({
  key: item.key,
  kind: 'queue',
  conversationId: item.conversation_id,
  item,
});

/** THE Needs-you number — the tab badge, the KPI tile, and the list all read it. */
export function needsYouCount(needs: NeedsYou, activeEsc: ActiveEscSummary | null): number {
  return needs.queueItems.length + needs.draftItems.length + (activeEsc?.opsCount ?? 0);
}

/** Render the count as "N+": a page of the source lists came back full. */
export function needsYouCapped(needs: NeedsYou, activeEsc: ActiveEscSummary | null): boolean {
  return needs.draftsCapped || Boolean(activeEsc?.capped);
}

// ---------------------------------------------------------------------------
// Selection — when the list picks a row for you (D-12).
// ---------------------------------------------------------------------------

/** Everything the auto-select decision depends on. */
export interface AutoSelectInput {
  /** The viewport is Tailwind's `lg` or wider — BOTH panes are on screen. */
  lgUp: boolean;
  /** A row is selected right now. */
  hasSelection: boolean;
  /** How many rows the merged list is showing. */
  rowCount: number;
  /** The `?review=` deep link has been consumed (or there never was one). */
  deepLinkSettled: boolean;
  /** The raw `?review=` param, exactly as the console read it. */
  reviewParam: string | null;
}

/**
 * Should the list preselect its first row?
 *
 * From `lg` up the answer is yes whenever nothing is selected: the two panes
 * sit side by side, so an empty right half is wasted space and a recruiter who
 * opens the console wants the oldest item already open.
 *
 * BELOW `lg` THE ANSWER IS ALWAYS NO, and that is the whole point of this
 * predicate. There, the list and the detail are the same column — the list is
 * hidden the moment something is selected — so auto-selecting would open a
 * detail view the recruiter never asked for, with the queue they came to read
 * nowhere on screen. The list has to be the landing view; a tap picks the row.
 *
 * The deep-link clause is unchanged and independent of tier: while an
 * unconsumed `?review=` exists, this holds off, because that effect and this
 * one fire in the same commit and the last setState would eat the deep link.
 */
export function shouldAutoSelect(input: AutoSelectInput): boolean {
  if (!input.lgUp) return false;
  if (input.hasSelection || input.rowCount === 0) return false;
  return input.deepLinkSettled || !input.reviewParam;
}

/**
 * Below `lg`, a selection whose row has LEFT the list (determination submitted,
 * flag resolved, draft approved) must be DROPPED, not remembered. On a laptop
 * the auto-select immediately overwrites a dead key with `rows[0]`, so a stale
 * key cannot survive there — but a phone never auto-selects, and the
 * `review-`/`screen-` keys are conversation-scoped: if that conversation later
 * re-enters the queue, a remembered dead key would re-match and yank the phone
 * from the list into a detail nobody tapped (seam-5 L3).
 */
export function shouldClearStaleSelection(
  lgUp: boolean,
  selectedKey: string | null,
  selectionExists: boolean
): boolean {
  return !lgUp && !!selectedKey && !selectionExists;
}
